/* Shared (counterparty) read-only view of a single transaction.
 * Auth is via signed JWT in the ?t= query param. No login required.
 * Route: /shared/:tx_id?t=...
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { FileDown, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  open: "bg-[#E6EFEC] text-[#0B4636]",
  closing: "bg-[#FFF4DB] text-[#8B5E00]",
  closed: "bg-[#E5E5DF] text-[#0A0A0A]",
  matured: "bg-[#102968] text-white",
};
const PATH_LABEL = {
  spv_trust: "SPV Trust Setup",
  discount_sale: "Outright Sale",
  financing: "Collateral Loan",
};

const fmt$ = (n) => (typeof n === "number" ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—");
const fmtTs = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleString(); } catch { return s; } };

export default function SharedTransaction() {
  const { tx_id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing share token. The link you used appears invalid."); return; }
    axios.get(`${API}/shared/${tx_id}?t=${encodeURIComponent(token)}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, [tx_id, token]);

  const downloadBinder = async () => {
    setDownloading(true);
    try {
      const r = await axios.post(`${API}/shared/${tx_id}/binder?t=${encodeURIComponent(token)}`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `closing_binder_${tx_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ }
    setDownloading(false);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF6] flex items-center justify-center p-6" data-testid="shared-error">
        <div className="bg-white border border-[#9E2A2B] rounded-sm p-8 max-w-md text-center">
          <AlertTriangle className="mx-auto text-[#9E2A2B] mb-3" size={32} />
          <div className="font-serif-display text-[20px] mb-2">Share link unavailable</div>
          <div className="text-[12.5px] text-[#5C5C5C]">{error}</div>
          <div className="text-[10px] font-mono-fin uppercase tracking-[0.16em] text-[#8A8A8A] mt-4">
            tx {tx_id?.slice(0, 16)}…
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FAFAF6] flex items-center justify-center" data-testid="shared-loading">
        <Loader2 className="animate-spin text-[#0B4636]" size={28} />
      </div>
    );
  }

  const tx = data.transaction;
  const replay = data.replay;

  return (
    <div className="min-h-screen bg-[#FAFAF6] text-[#0A0A0A]" data-testid="shared-view">
      <div className="border-b border-[#E5E5DF] bg-white">
        <div className="max-w-[1100px] mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono-fin uppercase tracking-[0.18em] text-[#5C5C5C] flex items-center gap-1.5">
              <ShieldCheck size={11} className="text-[#0B4636]" /> Counterparty Read-Only Access · JWT-Signed
            </div>
            <h1 className="font-serif-display text-[22px] leading-tight mt-1">{tx.trust_name}</h1>
            <div className="text-[11px] text-[#5C5C5C] mt-0.5">
              ↔ {tx.counterparty_name} · {PATH_LABEL[tx.path] || tx.path} · audience: <span className="font-mono-fin">{data.audience}</span>
            </div>
          </div>
          <button
            className="btn-secondary"
            onClick={downloadBinder}
            disabled={downloading}
            data-testid="shared-binder-btn"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            Download Closing Binder
          </button>
          <Link to="/" data-testid="shared-home-link" className="ml-3 text-[12px] text-[#5C5C5C] hover:text-[#0A0A0A]">Home</Link>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 py-8 space-y-6">
        {/* Deal economics */}
        <div className="bg-white border border-[#E5E5DF] rounded-sm p-5">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C5C5C] mb-3">Deal Economics</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Status" value={<span className={`text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-sm ${STATUS_COLORS[tx.status] || ""}`}>{tx.status}</span>} />
            <Stat label="Face Value" value={fmt$(tx.face_value)} mono />
            <Stat label="Discount Price" value={fmt$(tx.purchase_price)} mono />
            <Stat label="Discount" value={fmt$(tx.face_value - tx.purchase_price)} mono accent="#9E2A2B" />
            <Stat label="Coupon" value={`${Number(tx.coupon_rate).toFixed(2)}%`} mono />
            <Stat label="Term" value={`${tx.term_days} days`} mono />
            <Stat label="Execution" value={tx.execution_date} mono />
            <Stat label="Maturity" value={tx.maturity_date} mono />
          </div>
        </div>

        {/* Benchmark drift */}
        {replay?.benchmark_drift && (
          <div className="bg-white border border-[#102968] rounded-sm p-5" data-testid="shared-drift">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#102968] mb-2 font-bold">
              U.S. Treasury Benchmark Drift Across Holding Period
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-mono-fin text-[#5C5C5C] text-[14px]">{replay.benchmark_drift.start_rate.toFixed(3)}%</span>
              <span className="text-[#8A8A8A]">→</span>
              <span className="font-mono-fin text-[#102968] text-[18px] font-semibold">{replay.benchmark_drift.end_rate.toFixed(3)}%</span>
              <span className={`ml-auto text-[13px] font-mono-fin font-semibold ${replay.benchmark_drift.delta_bps >= 0 ? "text-[#9E2A2B]" : "text-[#0B4636]"}`}>
                {replay.benchmark_drift.delta_bps >= 0 ? "+" : ""}{replay.benchmark_drift.delta_bps} bps
              </span>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="bg-white border border-[#E5E5DF] rounded-sm p-5">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C5C5C] mb-3">
            Lifecycle &amp; Audit Trail · {replay.count} events
          </div>
          <div className="space-y-2" data-testid="shared-timeline">
            {replay.timeline.map((ev, i) => (
              <div key={i} className={`border-l-4 ${ev.kind === "treasury_delta" ? "border-l-[#102968] bg-[#F0F4FA]" : "border-l-[#0B4636] bg-[#FAFAF6]"} px-3 py-2 rounded-sm`}>
                <div className="flex items-center justify-between">
                  <div className="font-mono-fin text-[12.5px] font-semibold">{ev.type}</div>
                  <div className="text-[10.5px] font-mono-fin text-[#8A8A8A]">{fmtTs(ev.ts)}</div>
                </div>
                {ev.treasury_benchmark?.avg_interest_rate && (
                  <div className="text-[10.5px] text-[#5C5C5C] mt-0.5">
                    Stamped at Treasury benchmark <span className="font-mono-fin text-[#0B4636]">{Number(ev.treasury_benchmark.avg_interest_rate).toFixed(3)}%</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="text-center text-[10.5px] text-[#8A8A8A] font-mono-fin uppercase tracking-[0.12em] pt-4">
          Expires {fmtTs(data.expires_at)} · Read-only · SEUTRADE FINANCIAL SERVICES Note Liquidity Workbench
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, mono, accent }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#8A8A8A]">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono-fin" : ""} text-[14.5px] font-semibold`} style={accent ? { color: accent } : {}}>
        {value}
      </div>
    </div>
  );
}
